/**
 * GridPager — the ONE shared grid footer / pagination bar (CLAUDE.md §18.6
 * "Grid footer / pagination"): LEFT `Page X of N (TOTAL items)` · CENTRE
 * prev / numbered pages (with … windows) / next, current page in the theme
 * orange · RIGHT `Page size:` dropdown.
 *
 * <AppDataGrid> renders it for every standard grid. A hand-rolled table that
 * pages client-side (e.g. MDL Studies ▸ Loaded Samples, a detail view's child
 * grid with `paged`) renders it directly so its footer is identical to Sample
 * Registration's — never a bespoke pager.
 *
 * An EMPTY grid shows no footer (owner 2026-09-24, ALPACA parity): DevExpress
 * renders only "No data to display" when there are no rows, so `total === 0`
 * returns null.
 */
import type { ReactNode } from 'react';
import './appGrid.css';

export interface GridPagerProps {
  /** Optional hint shown on the RIGHT, just before "Page size:" (e.g. Daily QC's
   *  "Double-click a row to open it."). Opt-in — omitted, the footer is unchanged.
   *  Not the same as `note`, which sits on the LEFT with the page count. */
  footerNote?: ReactNode;
  /** Total row count across all pages. */
  total: number;
  /** Current 1-based page (clamped to the valid range here). */
  page: number;
  pageSize: number;
  onPageChange: (page: number) => void;
  onPageSizeChange: (pageSize: number) => void;
  pageSizeOptions?: readonly number[];
  /** OPTIONAL contextual microcopy shown after the page count, in the footer's
   *  LEFT (muted) group — e.g. a child grid's "Configured in …" note, which had
   *  its own footer before that footer became this shared one. The three zones
   *  are unchanged: this sits with the count, not in place of it. */
  note?: ReactNode;
  /** OPTIONAL — render the footer even when there is nothing to page through.
   *  The default (false) hides it on an empty grid, matching DevExpress, which
   *  shows only "No data to display" (owner 2026-09-24). Opt in per grid where
   *  the footer should stay put so the card does not change height as rows come
   *  and go — e.g. the Test Code Method Defaults tabs (owner request
   *  2026-10-13), and the Unfollowed Clients detail child grids (owner request
   *  2026-10-23), whose tabs are frequently empty. */
  alwaysShow?: boolean;
}

const DEFAULT_PAGE_SIZES = [10, 20, 50, 100] as const;

/** Build the page-button list with leading/trailing ellipsis windows. */
export function pageWindow(current: number, total: number): (number | 'gap')[] {
  if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1);
  const out: (number | 'gap')[] = [1];
  const start = Math.max(2, current - 1);
  const end = Math.min(total - 1, current + 1);
  if (start > 2) out.push('gap');
  for (let p = start; p <= end; p++) out.push(p);
  if (end < total - 1) out.push('gap');
  out.push(total);
  return out;
}

export function GridPager({
  total,
  page,
  pageSize,
  onPageChange,
  onPageSizeChange,
  pageSizeOptions = DEFAULT_PAGE_SIZES,
  note,
  footerNote,
  alwaysShow = false,
}: GridPagerProps) {
  if (total <= 0 && !alwaysShow) return null;
  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  const safePage = Math.min(Math.max(1, page), totalPages);

  return (
    <div className="adg-footer adg-pager">
      <div className="adg-foot-left">
        <span>
          Page {safePage} of {totalPages} ({total} item{total === 1 ? '' : 's'})
        </span>
        {note && <span className="adg-foot-note">{note}</span>}
      </div>

      {/* prev / numbered / next — the canonical order (standing rule 7).
          The two ARROWS are dropped on a single-page grid: they can only ever
          be disabled there, so they are dead controls, and in a narrow
          container (the PLM sequence rail) they crowd out the "Page size:"
          control that IS usable. The prototype's own footer shows just `1`
          for one page. Multi-page grids are untouched — the arrows appear the
          moment a second page exists. (Owner report 2026-09-25, marked
          against PLM-Analysis-v8.) */}
      <div className="adg-pages">
        {totalPages > 1 && (
        <button
          type="button"
          className="adg-pg-btn"
          disabled={safePage <= 1}
          onClick={() => onPageChange(safePage - 1)}
          aria-label="Previous page"
        >
          <i className="ti ti-chevron-left" />
        </button>
        )}
        {pageWindow(safePage, totalPages).map((p, idx) =>
          p === 'gap' ? (
            <span key={`gap-${idx}`} className="adg-pg-ellipsis">
              …
            </span>
          ) : (
            <button
              type="button"
              key={p}
              className={`adg-pg-btn${p === safePage ? ' active' : ''}`}
              onClick={() => onPageChange(p)}
              aria-label={`Page ${p}`}
              aria-current={p === safePage ? 'page' : undefined}
            >
              {p}
            </button>
          ),
        )}
        {totalPages > 1 && (
        <button
          type="button"
          className="adg-pg-btn"
          disabled={safePage >= totalPages}
          onClick={() => onPageChange(safePage + 1)}
          aria-label="Next page"
        >
          <i className="ti ti-chevron-right" />
        </button>
        )}
      </div>

      <div className="adg-foot-right">
        {footerNote && <span className="adg-foot-rnote">{footerNote}</span>}
        <span>Page size:</span>
        <select
          className="adg-size-sel"
          value={pageSize}
          onChange={(e) => onPageSizeChange(Number(e.target.value))}
          aria-label="Rows per page"
        >
          {pageSizeOptions.map((n) => (
            <option key={n} value={n}>
              {n}
            </option>
          ))}
        </select>
      </div>
    </div>
  );
}
